import { useEffect, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { EditorField, EdSelect, EdInput } from './editor-field';
import { ToggleSwitch } from './toggle-switch';
import { LotSizingEditor } from './lot-sizing-editor';
import type { ConditionEngineRules, DraftAction } from './editor-types';

function GridInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Grid Configuration">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>Grid trading opens multiple positions at fixed price intervals, building a basket of trades as the price moves. It profits from price oscillations within a range.</p>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Direction</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li><strong className="text-ink-2">Follow Entry</strong> — grid opens in the same direction as the triggering entry rule</li>
              <li><strong className="text-ink-2">BUY</strong> — grid always opens buy positions (DCA into longs)</li>
              <li><strong className="text-ink-2">SELL</strong> — grid always opens sell positions</li>
            </ul>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Step Type</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Absolute</span>
                <p className="text-[10px] text-ink-4">Fixed price distance (e.g., $5 between levels). Best for stable-priced instruments.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Percent</span>
                <p className="text-[10px] text-ink-4">Percentage-based distance (e.g., 0.5% between levels). Scales with price.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">ATR Multiple</span>
                <p className="text-[10px] text-ink-4">Based on Average True Range (e.g., 1.5× ATR). Adapts to current volatility.</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1.5">Example: Gold Grid</h3>
            <div className="bg-surface rounded-lg px-3 py-2.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>Step: <strong className="text-ink-1">$5 Absolute</strong>, Max Levels: <strong className="text-ink-1">5</strong></p>
              <p>Entry at: <strong className="text-ink-1">$2,000</strong></p>
              <p>Level 1: <strong className="text-green">$1,995</strong> (−$5)</p>
              <p>Level 2: <strong className="text-green">$1,990</strong> (−$10)</p>
              <p>Level 3: <strong className="text-green">$1,985</strong> (−$15)</p>
              <p>Level 4: <strong className="text-green">$1,980</strong> (−$20)</p>
              <p>Level 5: <strong className="text-green">$1,975</strong> (−$25)</p>
            </div>
            <p className="text-ink-3 mt-2">When price recovers back to $2,000, all grid positions close in profit.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Max Levels</h3>
            <p>Caps the number of grid positions that can be opened. Higher values increase potential profit from larger price swings but require more capital and increase risk.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function GridDirectionInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Grid Direction">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The direction grid levels open in when the price moves against the initial entry.</p>

          <div className="space-y-1.5">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Follow Entry</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Grid opens in the same direction as the triggering entry rule. If entry is BUY, grid adds more BUY positions (DCA down).</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-green">BUY</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Grid always opens buy positions regardless of the entry direction.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-red">SELL</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Grid always opens sell positions regardless of the entry direction.</p>
            </div>
          </div>

          <p className="text-ink-4">Most grid strategies use <strong className="text-ink-3">Follow Entry</strong> to average into the same direction.</p>
        </div>
      </Modal>
    </>
  );
}

function StepTypeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Grid Step Type">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>How the distance between grid levels is calculated.</p>

          <div className="space-y-1.5">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Absolute</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Fixed price distance. E.g., step = 5 → levels at $2,000, $1,995, $1,990...</p>
              <p className="text-[10px] text-ink-4">Best for: stable instruments like forex majors.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Percent</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Percentage of price. E.g., step = 0.5 → levels 0.5% apart.</p>
              <p className="text-[10px] text-ink-4">Best for: instruments with large price ranges (crypto, indices).</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">ATR Multiple</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Based on Average True Range. E.g., step = 1.5 → levels 1.5× ATR apart.</p>
              <p className="text-[10px] text-ink-4">Best for: volatility-adaptive grids that widen in choppy markets.</p>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function StepValueInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Grid Step Value">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The distance between each grid level. The meaning depends on the step type selected.</p>

          <div className="space-y-1.5">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">If Absolute</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Price distance. E.g., <strong className="text-ink-3">5</strong> = $5 between levels for gold, 5 pips for forex.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">If Percent</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Percentage. E.g., <strong className="text-ink-3">0.5</strong> = 0.5% of price between levels.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">If ATR Multiple</span>
              <p className="text-[10px] text-ink-4 mt-0.5">ATR multiplier. E.g., <strong className="text-ink-3">1.5</strong> = 1.5× the current ATR between levels.</p>
            </div>
          </div>

          <p className="text-ink-4">Smaller values create tighter grids (more trades, smaller profits per trade). Larger values create wider grids (fewer trades, larger profit per trade).</p>
        </div>
      </Modal>
    </>
  );
}

function MaxLevelsInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Max Grid Levels">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The maximum number of grid positions that can be opened as the price moves against you. Once this limit is reached, no more grid levels will open.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Max Levels = <strong className="text-ink-2">5</strong>, Step = $5 → grid covers a $25 range. After 5 positions, the grid stops adding even if price keeps falling.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Capital impact</h3>
            <p className="text-ink-3">With martingale lot sizing (2× multiplier), total capital needed grows exponentially:</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>5 levels: 31× base lot</p>
              <p>10 levels: 1,023× base lot</p>
              <p>15 levels: 32,767× base lot</p>
            </div>
          </div>

          <p className="text-ink-4">Balance max levels against your capital. More levels = more recovery potential but higher drawdown risk.</p>
        </div>
      </Modal>
    </>
  );
}

export function GridSection({
  draft,
  dispatch,
}: {
  draft: ConditionEngineRules;
  dispatch: React.Dispatch<DraftAction>;
}) {
  const grid = draft.entry.grid ?? {
    enabled: false,
    direction: 'FOLLOW_ENTRY' as const,
    stepType: 'ABSOLUTE' as const,
    stepValue: 5,
    maxGridLevels: 10,
    lotSizing: { type: 'FIXED' as const, volume: 0.01 },
  };

  // Ensure grid exists in draft
  useEffect(() => {
    if (!draft.entry.grid) {
      dispatch({ type: 'SET_PATH', path: 'entry.grid', value: grid });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <EditorField label={
        <span className="flex items-center gap-1">
          Enabled
          <GridInfo />
        </span>
      }>
        <ToggleSwitch
          value={grid.enabled}
          onChange={(v) => {
            if (v && !grid.stepType) {
              // Initialize full grid defaults when enabling for the first time
              dispatch({ type: 'SET_PATH', path: 'entry.grid', value: {
                enabled: true,
                direction: 'FOLLOW_ENTRY',
                stepType: 'ABSOLUTE',
                stepValue: grid.stepValue ?? 5,
                maxGridLevels: grid.maxGridLevels ?? 10,
                lotSizing: grid.lotSizing ?? { type: 'FIXED', volume: 0.01 },
              }});
            } else {
              dispatch({ type: 'SET_PATH', path: 'entry.grid.enabled', value: v });
            }
          }}
        />
      </EditorField>

      {grid.enabled && (
        <>
          <EditorField label={
            <span className="flex items-center gap-1">
              Direction
              <GridDirectionInfo />
            </span>
          }>
            <EdSelect
              value={grid.direction}
              onChange={(v) => dispatch({ type: 'SET_PATH', path: 'entry.grid.direction', value: v })}
              options={['FOLLOW_ENTRY', 'BUY', 'SELL']}
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Step Type
              <StepTypeInfo />
            </span>
          }>
            <EdSelect
              value={grid.stepType}
              onChange={(v) => dispatch({ type: 'SET_PATH', path: 'entry.grid.stepType', value: v })}
              options={['ABSOLUTE', 'PERCENT', 'ATR_MULTIPLE']}
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Step Value
              <StepValueInfo />
            </span>
          }>
            <EdInput
              value={grid.stepValue}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'entry.grid.stepValue', value: v })}
              step="any"
              small
            />
            <span className="text-[10px] text-ink-4">
              {grid.stepType === 'PERCENT' ? '%' : grid.stepType === 'ATR_MULTIPLE' ? '× ATR' : 'price'}
            </span>
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Max Levels
              <MaxLevelsInfo />
            </span>
          }>
            <EdInput
              value={grid.maxGridLevels}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'entry.grid.maxGridLevels', value: v })}
              min="1"
              step="1"
              small
            />
          </EditorField>

          <div className="text-[10px] font-bold text-ink-4 uppercase tracking-[0.5px] mt-2.5 mb-1.5">
            Grid Lot Sizing
          </div>
          <LotSizingEditor
            lot={grid.lotSizing}
            path="entry.grid.lotSizing"
            dispatch={dispatch}
          />
        </>
      )}
    </>
  );
}
